import { useTranslation } from 'react-i18next'
import { Copy } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SwitchField } from '@/components/SwitchField'
import type { AdvertiserAddress, AdvertiserEntityType } from '@/services/advertisers/advertisersApi'
import type { EntityDraft } from '@/utils/advertiserPolitical'

export const LEGAL_ENTITY_TYPES = [
  'CORPORATE_ENTITY_PUBLIC',
  'CORPORATE_ENTITY_PRIVATE',
  'POLITICAL_PARTY',
  'INDIVIDUAL',
] as const

const LEGAL_ENTITY_KEYS: Record<string, string> = {
  CORPORATE_ENTITY_PUBLIC: 'political.legalPublic',
  CORPORATE_ENTITY_PRIVATE: 'political.legalPrivate',
  POLITICAL_PARTY: 'political.legalParty',
  INDIVIDUAL: 'political.legalIndividual',
}

/** Translation key for an advertiser or entity legal type. */
export function legalEntityKey(type: string): string | undefined {
  return LEGAL_ENTITY_KEYS[type]
}

const ENTITY_LABEL_KEYS: Record<AdvertiserEntityType, string> = {
  CONTROLLING: 'advertisers.controllingEntity',
  PAYING: 'advertisers.payingEntity',
}

export function entityLabelKey(type: AdvertiserEntityType): string {
  return ENTITY_LABEL_KEYS[type]
}

const ADDRESS_FIELDS: { key: keyof AdvertiserAddress; label: string; full?: boolean }[] = [
  { key: 'addressLine1', label: 'address.line1', full: true },
  { key: 'addressLine2', label: 'address.line2', full: true },
  { key: 'city', label: 'address.city' },
  { key: 'state', label: 'address.stateRegion' },
  { key: 'postCode', label: 'address.postCode' },
  { key: 'country', label: 'address.country' },
]

export interface AdvertiserEntityFieldsProps {
  type: AdvertiserEntityType
  value: EntityDraft
  onChange: (value: EntityDraft) => void
  /** Fills the entity in from the advertiser's own details. */
  onCopyFromAdvertiser: () => void
}

/**
 * The controlling or paying entity behind a political advertiser. Most
 * advertisers play both roles themselves; only when one does not are the
 * entity's details asked for.
 */
export function AdvertiserEntityFields({ type, value, onChange, onCopyFromAdvertiser }: AdvertiserEntityFieldsProps) {
  const { t } = useTranslation()
  const label = t(entityLabelKey(type))
  const set = (patch: Partial<EntityDraft>) => onChange({ ...value, ...patch })

  return (
    <div className="flex flex-col gap-3">
      <SwitchField
        label={t('advertisers.entityIsAdvertiser', { entity: label })}
        description={t('advertisers.entityIsAdvertiserHelp')}
        checked={value.same}
        onCheckedChange={(same) => set({ same })}
      />
      {!value.same && (
        <div className="flex flex-col gap-3 pl-3">
          <div>
            <Button type="button" variant="outline" size="sm" onClick={onCopyFromAdvertiser}>
              <Copy className="size-3.5" />
              {t('advertisers.copyFromAdvertiser')}
            </Button>
          </div>
          <div className="field-grid">
            <div className="p-field">
              <label>{t('advertisers.legalName')}</label>
              <Input value={value.legalName} onChange={(e) => set({ legalName: e.target.value })} />
            </div>
            <div className="p-field">
              <label>{t('political.legalEntity')}</label>
              <Select
                label={t('political.legalEntity')}
                value={value.legalEntity}
                onValueChange={(v) => set({ legalEntity: v ?? '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('political.selectLegalEntity')}>
                    {value.legalEntity ? t(LEGAL_ENTITY_KEYS[value.legalEntity] ?? value.legalEntity) : null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {LEGAL_ENTITY_TYPES.map((et) => (
                    <SelectItem key={et} value={et}>
                      {t(LEGAL_ENTITY_KEYS[et])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-field">
              <label>{t('political.contactEmail')}</label>
              <Input
                type="email"
                value={value.contactEmail}
                onChange={(e) => set({ contactEmail: e.target.value })}
                placeholder={t('political.contactEmailPlaceholder')}
              />
            </div>
            <div className="p-field">
              <label>{t('common.website')}</label>
              <Input
                value={value.url}
                onChange={(e) => set({ url: e.target.value })}
                placeholder={t('advertisers.websitePlaceholder')}
              />
            </div>
            <div className="p-field">
              <label>{t('political.vatId')}</label>
              <Input
                value={value.vatId}
                onChange={(e) => set({ vatId: e.target.value })}
                placeholder={t('political.vatIdPlaceholder')}
              />
            </div>
            {ADDRESS_FIELDS.map((field) => (
              <div key={field.key} className={field.full ? 'p-field full' : 'p-field'}>
                <label>{t(field.label)}</label>
                <Input
                  value={value.address[field.key]}
                  onChange={(e) => set({ address: { ...value.address, [field.key]: e.target.value } })}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
